import { SubjectStatus } from '../../../types';
import type { ChipTone } from '../../common/StatusChip';

/**
 * How a paper's status reads everywhere marks are shown or entered — Enter
 * Marks, a student's results and the paper history — so the words and
 * colours never drift between screens.
 */

export interface PaperStatusMeta {
  label: string;
  /** Short form for dense cells and spreadsheets. */
  short: string;
  tone: ChipTone;
  hint: string;
}

export const PAPER_STATUS: Record<SubjectStatus, PaperStatusMeta> = {
  [SubjectStatus.PRESENT]: { label: 'Present', short: 'P', tone: 'success', hint: 'Sat the paper' },
  [SubjectStatus.ABSENT]: { label: 'Absent', short: 'A', tone: 'error', hint: 'Should have sat it but missed it' },
  [SubjectStatus.NOT_APPEARED]: { label: 'Not appeared', short: 'NA', tone: 'neutral', hint: "Wasn't expected to sit it, e.g. joined later — left out of the overall %" },
  [SubjectStatus.PENDING]: { label: 'Not entered', short: '—', tone: 'pending', hint: 'No mark yet' },
};

/** The statuses a person can choose for a paper, in the order they are offered. */
export const CHOOSABLE_STATUSES = [SubjectStatus.PRESENT, SubjectStatus.ABSENT, SubjectStatus.NOT_APPEARED] as const;

/** What can be typed into a marks box: up to four digits and two decimals. */
export const MARK_INPUT = /^\d{0,4}(\.\d{0,2})?$/;

/** A paper's mark as it reads in a cell: "42", "Absent", "Not appeared" or "—". */
export const paperText = (p: { status: SubjectStatus; obtainedMarks: number | null }) =>
  p.status === SubjectStatus.PRESENT && p.obtainedMarks != null ? String(p.obtainedMarks) : PAPER_STATUS[p.status].label;

/** A paper being edited: what is typed and the status chosen. */
export interface PaperDraft {
  marks: string;
  status: SubjectStatus;
}

export const draftOf = (p: { status: SubjectStatus; obtainedMarks: number | null }): PaperDraft => ({
  status: p.status,
  marks: p.status === SubjectStatus.PRESENT && p.obtainedMarks != null ? String(p.obtainedMarks) : '',
});

/** A draft as the server records it, or a reason it can't be yet. */
export const draftProblem = (d: PaperDraft, maxMarks: number): string | null => {
  if (d.status !== SubjectStatus.PRESENT) return null;
  if (d.marks === '' || d.marks.endsWith('.')) return 'Enter a mark';
  if (Number(d.marks) > maxMarks) return `Can't be more than ${maxMarks}`;
  return null;
};

export const sameDraft = (a: PaperDraft, b: PaperDraft) =>
  a.status === b.status && (a.status !== SubjectStatus.PRESENT || Number(a.marks) === Number(b.marks));

/** Typing a mark makes the student present; clearing it makes the paper not entered. */
export const typeMark = (raw: string): PaperDraft | null =>
  MARK_INPUT.test(raw) ? { marks: raw, status: raw === '' ? SubjectStatus.PENDING : SubjectStatus.PRESENT } : null;

/** Choosing a status; choosing the current one again clears the paper. */
export const chooseStatus = (current: PaperDraft, status: SubjectStatus): PaperDraft => {
  const next = current.status === status ? SubjectStatus.PENDING : status;
  return { status: next, marks: next === SubjectStatus.PRESENT ? current.marks : '' };
};

export const entryOf = (d: PaperDraft) => ({
  status: d.status,
  obtainedMarks: d.status === SubjectStatus.PRESENT ? Number(d.marks) : null,
});
